<template>
    <BModal
        :model-value="modal"
        size="lg"
        centered
        hide-footer
        no-close-on-backdrop
        no-close-on-esc
        @update:modelValue="updateModal"
        title="Payment"
    >
        <div v-if="isDataLoading" class="text-center py-5">
            <BSpinner variant="success" label="Spinning"></BSpinner>
            <p class="mt-2 text-muted">Please wait...</p>
        </div>
        <BTabs v-else v-model="activeTab" content-class="mt-3" fill>
            <BTab title="Payment Form">
                <div v-if="remainingBill === 0 && !isEditMode" class="alert alert-success text-center py-4 my-2">
                    <i class="fas fa-check-circle fa-2x mb-3"></i>
                    <h5>This offer is already fully paid!</h5>
                    <p class="mb-0 text-muted text-white">Check payment history in the next tab.</p>
                </div>
                <div v-else>
                    <BForm @submit.stop.prevent="submitPayment">
                        <BRow>
                            <BCol md="6">
                                <BFormGroup label="Offer No">
                                    <BFormInput :model-value="offer?.offer_no" disabled />
                                </BFormGroup>
                            </BCol>
                            <BCol md="6">
                                <BFormGroup :label="offer?.category === 'Agent' ? 'Agent Name' : 'Guest Name'">
                                    <BFormInput :model-value="offer?.guest?.fullname" disabled />
                                </BFormGroup>
                            </BCol>
                        </BRow>
                        <BRow class="mt-2">
                            <BCol md="6">
                                <BFormGroup label="Grand Total Sales (IDR)">
                                    <div class="d-flex flex-column">
                                        <BFormInput :model-value="formatRupiah(offer?.grand_total_final > 0 ? offer?.grand_total_final : offer?.grand_total)" disabled />
                                        <small v-if="offer?.currency?.code !== 'IDR'" class="text-muted mt-1 italic">
                                            ({{ formatCurrency((offer?.grand_total_final > 0 ? offer?.grand_total_final : offer?.grand_total) / effectiveRate, offer?.currency) }})
                                        </small>
                                    </div>
                                </BFormGroup>
                            </BCol>
                            <BCol md="6">
                                <BFormGroup label="Remaining Bill">
                                    <div class="d-flex flex-column">
                                        <BFormInput 
                                            :model-value="formatRupiah(effectiveRemainingBill)" 
                                            disabled 
                                            class="text-danger fw-bold" 
                                        />
                                        <small v-if="offer?.currency?.code !== 'IDR'" class="text-danger mt-1 italic">
                                            ({{ formatCurrency(effectiveRemainingBill / effectiveRate, offer?.currency) }})
                                        </small>
                                    </div>
                                </BFormGroup>
                            </BCol>
                        </BRow>

                        <hr />

                        <BRow>
                            <BCol md="6">
                                <BFormGroup label="Payment Date *">
                                    <BFormInput type="date" v-model="form.payment_date" :class="{'is-invalid': v$.form.payment_date.$error}" />
                                    <span class="text-danger small" v-if="v$.form.payment_date.$error">Date is required</span>
                                </BFormGroup>
                            </BCol>
                            <BCol md="6">
                                <BFormGroup label="Payment Method *">
                                    <v-select
                                        v-model="form.payment_method"
                                        :options="paymentMethodList"
                                        label="name"
                                        :reduce="item => item"
                                        placeholder="Select Method"
                                        :class="{'is-invalid': v$.form.payment_method.$error}"
                                    />
                                    <span class="text-danger small" v-if="v$.form.payment_method.$error">Method is required</span>
                                </BFormGroup>
                            </BCol>
                        </BRow>

                        <BRow class="mt-2">
                            <BCol md="12">
                                <BFormGroup label="Payment Type *">
                                    <v-select
                                        v-model="form.payment_type"
                                        :options="['DP', 'Fullpayment']"
                                        placeholder="Select Type"
                                        @update:modelValue="handleTypeChange"
                                        :class="{'is-invalid': v$.form.payment_type.$error}"
                                    />
                                    <span class="text-danger small" v-if="v$.form.payment_type.$error">Type is required</span>
                                </BFormGroup>
                            </BCol>
                        </BRow>

                        <BRow class="mt-2" v-if="isEditMode">
                            <BCol md="12">
                                <div class="alert alert-info py-2 mb-0">
                                    <strong>Editing Payment</strong>
                                    <BButton variant="link" size="sm" class="float-end p-0 text-decoration-none" @click="cancelEdit">Cancel Edit</BButton>
                                </div>
                            </BCol>
                        </BRow>

                        <BRow class="mt-2" v-if="offer?.currency?.code !== 'IDR'">
                            <BCol md="6">
                                <BFormGroup label="Payment Currency *">
                                    <v-select
                                        v-model="form.payment_currency"
                                        :options="[
                                            { label: 'Indonesian Rupiah (IDR)', value: 'IDR' },
                                            { label: `${offer?.currency?.name} (${offer?.currency?.code})`, value: offer?.currency?.code }
                                        ]"
                                        :reduce="opt => opt.value"
                                        :clearable="false"
                                        placeholder="Select Currency"
                                        @update:model-value="onCurrencyChange"
                                    />
                                </BFormGroup>
                            </BCol>
                            
                            <BCol md="6" v-if="offer?.currency?.code !== 'IDR'">
                                <BFormGroup>
                                    <template #label>
                                        Exchange Rate * 
                                        <BButton variant="link" size="sm" class="p-0 ms-2 text-decoration-none" @click="resetToEffectiveRate" title="Reset to Offer Effective Rate">
                                            Reset
                                        </BButton>
                                    </template>
                                    <BFormInput 
                                        type="number" 
                                        v-model="form.exchange_rate" 
                                        placeholder="0" 
                                        step="any"
                                        :disabled="form.payment_type === 'Fullpayment' && !isEditMode"
                                        @input="onRateInput"
                                    />
                                    <small class="text-info d-block mt-1 font-weight-bold">
                                        1 {{ offer?.currency?.code }} = {{ Number(effectiveRate).toFixed(10).replace(/\.?0+$/, "") }} IDR
                                    </small>
                                </BFormGroup>
                            </BCol>
                        </BRow>

                        <BRow class="mt-2">
                            <BCol :md="offer?.currency?.code !== 'IDR' ? 6 : 12">
                                <BFormGroup label="Amount (IDR) *">
                                    <BFormInput 
                                        type="number" 
                                        v-model="form.amount" 
                                        placeholder="0" 
                                        :disabled="(form.payment_type === 'Fullpayment' && !isEditMode) || (form.payment_currency !== 'IDR')"
                                        @input="onIdrInput"
                                        :class="{'is-invalid': v$.form.amount.$error}" 
                                    />
                                </BFormGroup>
                            </BCol>
                            <BCol md="6" v-if="offer?.currency?.code !== 'IDR'">
                                <BFormGroup :label="`Amount (${offer?.currency?.code}) *` ">
                                    <BFormInput 
                                        type="number" 
                                        v-model="form.foreign_amount" 
                                        placeholder="0" 
                                        step="any"
                                        :disabled="(form.payment_type === 'Fullpayment' && !isEditMode) || (form.payment_currency === 'IDR')"
                                        @input="onForeignInput"
                                    />
                                </BFormGroup>
                            </BCol>
                        </BRow>
                        <BRow v-if="v$.form.amount.$error">
                             <BCol md="12">
                                <span class="text-danger small">
                                    {{ v$.form.amount.$errors[0]?.$message }}
                                </span>
                             </BCol>
                        </BRow>

                        <BRow class="mt-2">
                            <BCol md="12">
                                <BFormGroup label="Remarks">
                                    <BFormTextarea v-model="form.remarks" rows="2" />
                                </BFormGroup>
                            </BCol>
                        </BRow>

                        <div class="d-flex justify-content-end mt-4">
                            <BButton variant="secondary" class="me-2" @click="updateModal(false)">Close</BButton>
                            <BButton :variant="isEditMode ? 'warning' : 'primary'" type="submit">
                                {{ isEditMode ? 'Update Payment' : 'Submit Payment' }}
                            </BButton>
                        </div>
                    </BForm>
                </div>
            </BTab>

            <BTab title="Payment History">
                <div class="table-responsive">
                    <table class="table table-bordered table-striped table-sm">
                        <thead class="table-light">
                            <tr>
                                <th>Date</th>
                                <th>Method</th>
                                <th>Type</th>
                                <th class="text-end">Amount</th>
                                <th class="text-center" v-if="offer?.currency?.code !== 'IDR'">Rate</th>
                                <th>Remarks</th>
                                <th class="text-center" style="width: 80px">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="payment in historyList" :key="payment.uuid">
                                <td>{{ formatDate(payment.payment_date) }}</td>
                                <td>{{ payment.payment_method?.name }}</td>
                                <td class="text-center">
                                    <BBadge :variant="payment.payment_type === 'Fullpayment' ? 'success' : 'info'">
                                        {{ payment.payment_type }}
                                    </BBadge>
                                </td>
                                <td class="text-end fw-medium">
                                    {{ formatRupiah(payment.amount) }}
                                    <div v-if="payment.amount_foreign > 0" class="small text-muted italic">
                                        {{ formatCurrency(payment.amount_foreign, props.offer?.currency) }}
                                    </div>
                                </td>
                                <td class="text-center text-muted small" v-if="offer?.currency?.code !== 'IDR'">
                                    {{ Number(payment.exchange_rate || 0).toLocaleString('id-ID', { maximumFractionDigits: 2 }) }}
                                </td>
                                <td>{{ payment.remarks || '-' }}</td>
                                <td class="text-center">
                                    <div class="d-flex justify-content-center gap-1">
                                        <BButton variant="light" size="sm" class="p-1" title="Edit" @click="editPayment(payment)">
                                            <i class="fas fa-edit text-warning"></i>
                                        </BButton>
                                        <BButton variant="light" size="sm" class="p-1" title="Delete" @click="deletePayment(payment)">
                                            <i class="fas fa-trash text-danger"></i>
                                        </BButton>
                                    </div>
                                </td>
                            </tr>
                             <tr v-if="historyList.length === 0 && !isDataLoading">
                                <td colspan="7" class="text-center py-3">No payment history found.</td>
                            </tr>
                        </tbody>
                        <tfoot v-if="historyList.length > 0" class="table-light fw-bold">
                            <tr>
                                <td colspan="5" class="text-end">Grand Total:</td>
                                <td class="text-end">
                                    {{ formatRupiah(offer?.grand_total_final > 0 ? offer?.grand_total_final : offer?.grand_total) }}
                                </td>
                                <td class="text-end" v-if="offer?.currency?.code !== 'IDR' ">
                                    {{ formatCurrency(offer?.grand_total_foreign || 0, offer?.currency) }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="5" class="text-end">Total Paid:</td>
                                <td class="text-end text-success">
                                    {{ formatRupiah(historyList.reduce((acc, curr) => acc + Number(curr.amount), 0)) }}
                                </td>
                                <td class="text-end text-success" v-if="offer?.currency?.code !== 'IDR'">
                                    {{ formatCurrency((historyList.reduce((acc, curr) => acc + Number(curr.amount), 0)) / effectiveRate, offer?.currency) }}
                                </td>
                            </tr>
                            <tr>
                                <td colspan="5" class="text-end">Remaining Bill:</td>
                                <td class="text-end text-danger">
                                    {{ formatRupiah(remainingBill) }}
                                </td>
                                <td class="text-end text-danger" v-if="offer?.currency?.code !== 'IDR'">
                                    {{ formatCurrency(remainingBill / effectiveRate, offer?.currency) }}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </BTab>
        </BTabs>
        <template #footer>
            <div></div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, reactive, computed, inject } from "vue";
import { BModal, BTabs, BTab, BRow, BCol, BForm,  BFormGroup, BFormInput, BFormTextarea, BButton, BBadge, BSpinner } from "bootstrap-vue-next";
import axios from "axios";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import { useLoading } from "vue-loading-overlay";
import useVuelidate from "@vuelidate/core";
import { required, minValue, helpers } from "@vuelidate/validators";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { formatRupiah, formatDate } from "@/utils/format";

const swal = inject("$swal");

const formatCurrency = (val, currency) => {
    if (!currency || currency.code === 'IDR') return formatRupiah(val);
    // Try to use a standard format for common currencies or customized locale
    try {
        const formatter = new Intl.NumberFormat(currency.locale || 'en-US', {
            style: 'currency',
            currency: currency.code
        });
        return formatter.format(Number(val || 0));
    } catch (e) {
        return `${currency.symbol} ${Number(val || 0).toLocaleString()}`;
    }
};

const props = defineProps({
    modal: Boolean,
    offer: Object
});

const emit = defineEmits(["update:modal", "refresh"]);
const auth = useAuthStore();
const toast = useToast();
const $loading = useLoading();

const historyList = ref([]);
const paymentMethodList = ref([]);
const isDataLoading = ref(false); // Ref untuk internal tab loading

const form = reactive({
    amount: "",
    foreign_amount: "",
    exchange_rate: 0,
    payment_currency: "IDR",
    payment_method: null,
    payment_date: new Date().toISOString().substr(0, 10),
    payment_type: null,
    remarks: ""
});

const isEditMode = ref(false);
const editingUuid = ref(null);
const activeTab = ref(0); // Add activeTab control

const effectiveRate = computed(() => {
    if (!props.offer) return 1;
    const finalIdr = props.offer.grand_total_final > 0 ? props.offer.grand_total_final : props.offer.grand_total;
    const foreign = Number(props.offer.grand_total_foreign || 0);
    // If foreign and IDR both > 0, we can compute an effective rate
    if (foreign > 0 && finalIdr > 0) {
        return finalIdr / foreign;
    }
    return Number(props.offer.exchange_rate || 1);
});

const remainingBill = computed(() => {
    if (!props.offer) return 0;
    const totalPaid = historyList.value.reduce((acc, curr) => acc + Number(curr.amount), 0);
    const targetTotal = props.offer.grand_total_final > 0 ? props.offer.grand_total_final : props.offer.grand_total;
    return Math.max(0, Number(targetTotal) - totalPaid);
});

const effectiveRemainingBill = computed(() => {
    const originalAmount = isEditMode.value ? Number(historyList.value.find(p => p.uuid === editingUuid.value)?.amount || 0) : 0;
    return Number(remainingBill.value) + originalAmount;
});

const handleTypeChange = (val) => {
    if (val === 'Fullpayment') {
        if (isEditMode.value) {
            const originalAmount = Number(historyList.value.find(p => p.uuid === editingUuid.value)?.amount || 0);
            form.amount = Number(remainingBill.value) + originalAmount;
        } else {
            form.amount = remainingBill.value;
        }
        onIdrInput();
    } else {
        // If not full payment, default to deposit amount if available and no history, otherwise 0
        if (historyList.value.length === 0 && props.offer?.deposit_amount > 0) {
            form.amount = props.offer.deposit_amount;
        } else if (!isEditMode.value) {
            form.amount = 0;
        }
        onIdrInput();
    }
};

const onCurrencyChange = (val) => {
    // If switching to foreign currency, ensure foreign_amount is Master
    if (val !== 'IDR') {
        if (Number(form.amount) > 0 && !form.foreign_amount) onIdrInput();
    } else {
        // IDR is Master
        if (Number(form.foreign_amount) > 0 && !form.amount) onForeignInput();
    }
};

const resetToEffectiveRate = () => {
    form.exchange_rate = effectiveRate.value;
    // Recalculate amounts based on current selection
    if (form.payment_currency === 'IDR') {
        onIdrInput(); // IDR is fixed, update Foreign
    } else {
        onForeignInput(); // Foreign is fixed, update IDR
    }
};

const onIdrInput = () => {
    const rate = Number(form.exchange_rate || 1);
    form.foreign_amount = Number((Number(form.amount || 0) / rate).toFixed(2));
};

const onForeignInput = () => {
    const rate = Number(form.exchange_rate || 0);
    if (rate > 0) {
        form.amount = Math.round(Number(form.foreign_amount || 0) * rate);
    } else if (Number(form.amount) > 0) {
        // Rupiah + Dollar -> Rate logic
        form.exchange_rate = Number((Number(form.amount) / Number(form.foreign_amount || 1)).toFixed(4));
    }
};

const onRateInput = () => {
    // Rate changed -> update IDR based on Foreign if Foreign > 0, else IDR is fixed
    if (Number(form.foreign_amount) > 0) {
        form.amount = Math.round(Number(form.foreign_amount) * Number(form.exchange_rate || 0));
    } else if (Number(form.amount) > 0) {
        onIdrInput(); // Update Foreign
    }
};

const rules = {
    form: {
        amount: { 
            required, 
            minValue: minValue(1),
            maxVal: helpers.withMessage("Amount exceeds remaining bill", (val) => {
                return Number(val) <= effectiveRemainingBill.value;
            }) 
        },
        payment_type: { required },
        payment_method: { required },
        payment_date: { required }
    }
};

const v$ = useVuelidate(rules, { form });

watch(() => form.amount, (newVal) => {
    if (isEditMode.value && form.payment_type === 'Fullpayment') {
        // Calculate the total required for full payment
        const originalAmount = Number(historyList.value.find(p => p.uuid === editingUuid.value)?.amount || 0);
        const requiredForFull = remainingBill.value + originalAmount;
        
        // If the edited amount is less than the required amount, switch to DP
        if (Number(newVal) < requiredForFull) {
            form.payment_type = 'DP';
        }
    }
});

const updateModal = (val) => {
    emit("update:modal", val);
};

// const formatMoney = (val, currency) => {
//     if (!currency || currency.code === 'IDR') return formatRupiah(val);
//     const formatter = new Intl.NumberFormat(currency.locale || 'en-US', {
//         style: 'currency',
//         currency: currency.code
//     });
//     return formatter.format(Number(val || 0));
// };

const initData = async () => {
    if (!props.offer?.uuid) return;
    isDataLoading.value = true;
    try {
        // Jalankan fetch history dan payment methods secara paralel
        await Promise.all([
            fetchHistory(),
            fetchPaymentMethods()
        ]);
    } finally {
        isDataLoading.value = false;
        // Initialize data
        isDataLoading.value = false;
        form.exchange_rate = effectiveRate.value;
        form.payment_currency = props.offer?.currency?.code || 'IDR';
        
        // Auto-fill amount as deposit amount if no payment history yet and deposit is set
        if (historyList.value.length === 0 && props.offer?.deposit_amount > 0) {
             form.amount = props.offer.deposit_amount;
             form.payment_type = 'DP';
             if (form.payment_currency === 'IDR') {
                 onIdrInput();
             } else {
                 // Deposit is usually set in IDR in the model if I recall, 
                 // but let's assume if payment currency is Foreign, we want to calculate it.
                 onIdrInput();
             }
        }
    }
};

const fetchHistory = async () => {
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/offer-payment/${props.offer.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        if (res.data.status) historyList.value = res.data.data;
    } catch (e) {
        console.error("History fetch error:", e);
    }
};

const fetchPaymentMethods = async () => {
    if (paymentMethodList.value.length > 0) return;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/payment-method`, {
            headers: { Authorization: "Bearer " + auth._token }
        });
        if (res.data.status) paymentMethodList.value = res.data.data;
    } catch (e) {
        console.error("Payment method fetch error:", e);
    }
};

const editPayment = (payment) => {
    isEditMode.value = true;
    editingUuid.value = payment.uuid;
    
    form.amount = payment.amount;
    form.foreign_amount = payment.amount_foreign;
    form.exchange_rate = payment.exchange_rate || effectiveRate.value;
    form.payment_method = payment.payment_method;
    form.payment_date = payment.payment_date;
    form.payment_type = payment.payment_type;
    form.payment_currency = payment.payment_currency || 'IDR';
    form.remarks = payment.remarks;
    
    activeTab.value = 0; // Switch to Payment Form tab
};

const cancelEdit = () => {
    isEditMode.value = false;
    editingUuid.value = null;
    resetForm();
};

const resetForm = () => {
    form.amount = "";
    form.foreign_amount = "";
    form.payment_currency = props.offer?.currency?.code || 'IDR';
    form.payment_type = null;
    form.payment_method = null;
    form.remarks = "";
    form.payment_date = new Date().toISOString().substr(0, 10);
    form.exchange_rate = effectiveRate.value;
    v$.value.$reset();
};

const deletePayment = async (payment) => {
    const result = await swal({
        title: "Confirm Delete",
        text: `Are you sure you want to delete this payment (${formatRupiah(payment.amount)})?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "Cancel",
        customClass: {
            popup: "rounded",
            confirmButton: "btn btn-danger me-2",
            cancelButton: "btn btn-secondary",
        },
        buttonsStyling: false,
    });

    if (!result.value) return;
    
    const loader = $loading.show();
    try {
        const res = await axios.delete(`${process.env.VUE_APP_API_URL}/offer-payment/${payment.uuid}`, {
            headers: { Authorization: "Bearer " + auth._token }
        });

        if (res.data.status) {
            toast.success("Payment deleted successfully");
            await fetchHistory();
            emit("refresh");
        }
    } catch (e) {
        toast.error(e.response?.data?.message || "Failed to delete payment");
    } finally {
        loader.hide();
    }
};

const submitPayment = async () => {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    const loader = $loading.show();
    try {
        const payload = {
            offer_uuid: props.offer.uuid,
            guest_uuid: props.offer.guest_uuid,
            payment_method_uuid: form.payment_method.uuid,
            payment_currency: form.payment_currency,
            amount: form.amount,
            amount_foreign: props.offer?.currency?.code !== 'IDR' ? form.foreign_amount : 0,
            exchange_rate: props.offer?.currency?.code === 'IDR' ? 1 : form.exchange_rate,
            payment_date: form.payment_date,
            remarks: form.remarks,
            payment_type: form.payment_type,
        };

        let res;
        if (isEditMode.value) {
            res = await axios.put(`${process.env.VUE_APP_API_URL}/offer-payment/${editingUuid.value}`, payload, {
                headers: { Authorization: "Bearer " + auth._token }
            });
        } else {
            res = await axios.post(`${process.env.VUE_APP_API_URL}/offer-payment`, payload, {
                headers: { Authorization: "Bearer " + auth._token }
            });
        }

        if (res.data.status) {
            toast.success(isEditMode.value ? "Payment updated successfully" : "Payment recorded successfully");
            
            cancelEdit();
            
            // Refresh data dan tutup modal
            await fetchHistory();
            emit("refresh");
            updateModal(false); // Tutup modal setelah berhasil
        }
    } catch (e) {
        toast.error(e.response?.data?.message || "Failed to save payment");
    } finally {
        loader.hide();
    }
};

watch(() => props.modal, (val) => {
    if (val) {
        cancelEdit();
        activeTab.value = 0;
        initData();
    }
});
</script>